import React from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  IconButton,
  Drawer,
  useMediaQuery,
  useTheme,
  Divider,
  Collapse,
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  Menu as MenuIcon,
  ExpandLess,
  ExpandMore,
  AutoAwesome as AutoAwesomeIcon,
} from '@mui/icons-material';

export interface NavigationItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  count?: number;
  countVariant?: 'default' | 'alert';
  active?: boolean;
  disabled?: boolean;
  pulse?: boolean;
  /** Text of the pulsing badge rendered when `pulse` is set. Defaults to "LATEST". */
  pulseLabel?: string;
  /** Renders an attention-grabbing accent pill (e.g. "Early Access →") after the label. */
  tag?: { label: string };
  onClick: () => void;
}

export interface NavigationGroup {
  id: string;
  label: string;
  items: NavigationItem[];
}

interface SidebarNavigationProps {
  groups: NavigationGroup[];
  open: boolean;
  onClose: () => void;
  onToggle: () => void;
  showMobileToggle?: boolean;
}

const SidebarNavigation: React.FC<SidebarNavigationProps> = ({
  groups,
  open,
  onClose,
  onToggle,
  showMobileToggle = true,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const drawerWidth = 264;
  const [expandedGroups, setExpandedGroups] = React.useState<Set<string>>(
    new Set(['getting-started', ...groups.map(g => g.id)])
  );

  // Groups gated on the user's roles (Admin, Leader) are absent at mount and only
  // appear once /api/users/me resolves, so the initialiser above never sees them
  // and they used to render collapsed. Expand each group the first time it shows
  // up — tracked by id, so a group the user collapses by hand stays collapsed.
  const seenGroupIds = React.useRef<Set<string>>(
    new Set(['getting-started', ...groups.map(g => g.id)])
  );
  React.useEffect(() => {
    const unseen = groups.map(g => g.id).filter(id => !seenGroupIds.current.has(id));
    if (unseen.length === 0) return;
    unseen.forEach(id => seenGroupIds.current.add(id));
    setExpandedGroups(prev => {
      const next = new Set(prev);
      unseen.forEach(id => next.add(id));
      return next;
    });
  }, [groups]);

  const toggleGroup = (groupId: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  };

  // ── "Early Access" flag tab ───────────────────────────────────────────
  // On desktop the tagged item renders a tab that protrudes from the sidebar's
  // right edge, aligned with the item's row. The drawer clips its own content,
  // so the tab is rendered as a fixed element whose position we measure.
  const rootRef = React.useRef<HTMLDivElement | null>(null);
  const scrollRef = React.useRef<HTMLDivElement | null>(null);
  const listRef = React.useRef<HTMLUListElement | null>(null);
  const tagItemRef = React.useRef<HTMLLIElement | null>(null);
  const [tagPos, setTagPos] = React.useState<{ top: number; left: number } | null>(null);

  const taggedItem = React.useMemo(
    () => groups.flatMap(g => g.items).find(i => i.tag),
    [groups]
  );

  const measureTag = React.useCallback(() => {
    if (isMobile || !open) {
      setTagPos(null);
      return;
    }
    const row = tagItemRef.current;
    const scroller = scrollRef.current;
    const root = rootRef.current;
    if (!row || !scroller || !root) {
      setTagPos(null);
      return;
    }
    const rowRect = row.getBoundingClientRect();
    const scRect = scroller.getBoundingClientRect();
    const center = rowRect.top + rowRect.height / 2;
    // Hide when the row is collapsed (height 0) or scrolled out of view.
    if (rowRect.height === 0 || center < scRect.top + 6 || center > scRect.bottom - 6) {
      setTagPos(null);
      return;
    }
    setTagPos({ top: center, left: root.getBoundingClientRect().right });
  }, [isMobile, open]);

  React.useEffect(() => {
    measureTag();
    const ro = new ResizeObserver(() => measureTag());
    [rootRef.current, scrollRef.current, listRef.current].forEach((el) => {
      if (el) ro.observe(el);
    });
    window.addEventListener('resize', measureTag);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measureTag);
    };
  }, [measureTag, expandedGroups, groups, open, isMobile]);

  const drawerContent = (
    <Box
      sx={{
        width: drawerWidth,
        height: '100%',
        minHeight: 0,
        bgcolor: 'var(--color-bg)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Header with collapse button */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1.5,
          borderBottom: '1px solid',
          borderColor: 'rgba(var(--color-text-rgb), 0.1)',
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, color: 'var(--color-text-strong)', fontSize: '1.05rem' }}>
          Navigation
        </Typography>
        {isMobile ? (
          <IconButton
            size="small"
            onClick={onClose}
            aria-label="Close navigation"
            sx={{
              color: 'var(--color-text-strong)',
              '&:hover': {
                bgcolor: 'rgba(var(--color-text-rgb), 0.1)',
              },
            }}
          >
            <ChevronLeftIcon />
          </IconButton>
        ) : (
          <IconButton
            size="small"
            onClick={onToggle}
            sx={{
              color: 'var(--color-text-strong)',
              '&:hover': {
                bgcolor: 'rgba(var(--color-text-rgb), 0.1)',
              },
            }}
          >
            <ChevronLeftIcon />
          </IconButton>
        )}
      </Box>

      {/* Navigation Groups */}
      <Box
        ref={scrollRef}
        onScroll={measureTag}
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
          WebkitOverflowScrolling: 'touch',
          display: 'flex',
          flexDirection: 'column',
          overscrollBehavior: 'contain',
          '&::-webkit-scrollbar': {
            width: 8,
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(var(--color-text-rgb), 0.2)',
            borderRadius: 8,
          },
          '&::-webkit-scrollbar-thumb:hover': {
            backgroundColor: 'rgba(var(--color-text-rgb), 0.32)',
          },
        }}
      >
        <List ref={listRef} sx={{ pt: 0.5, px: 1, flexShrink: 0 }}>
        {groups.map((group, groupIndex) => {
          const isExpanded = expandedGroups.has(group.id);
          const hasActiveItem = group.items.some(item => item.active);
          
          return (
            <React.Fragment key={group.id}>
              {/* Group Header */}
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => toggleGroup(group.id)}
                  sx={{
                    px: 2,
                    py: 0.75,
                    borderRadius: 1,
                    '&:hover': {
                      bgcolor: 'rgba(var(--color-text-rgb), 0.05)',
                    },
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 600,
                      color: hasActiveItem ? 'var(--color-accent)' : 'var(--color-text-strong)',
                      fontSize: '1rem',
                      flex: 1,
                    }}
                  >
                    {group.label}
                  </Typography>
                  {isExpanded ? (
                    <ExpandLess sx={{ color: 'var(--color-text-strong)' }} />
                  ) : (
                    <ExpandMore sx={{ color: 'var(--color-text-strong)' }} />
                  )}
                </ListItemButton>
              </ListItem>

              {/* Group Items */}
              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {group.items.map((item) => (
                    <ListItem key={item.id} disablePadding ref={item.tag ? tagItemRef : undefined}>
                      <ListItemButton
                        data-nav-item-id={item.id}
                        disabled={item.disabled}
                        onClick={() => {
                          if (item.disabled) return;
                          item.onClick();
                          if (isMobile) {
                            onClose();
                          }
                        }}
                        selected={item.active}
                        sx={{
                          pl: 4,
                          pr: 2.5,
                          py: 0.75,
                          '&.Mui-selected': {
                            bgcolor: item.active ? 'rgba(var(--color-accent-rgb), 0.1)' : 'transparent',
                            borderLeft: item.active ? '3px solid var(--color-accent)' : 'none',
                            '&:hover': {
                              bgcolor: item.active ? 'rgba(var(--color-accent-rgb), 0.15)' : 'rgba(var(--color-text-rgb), 0.05)',
                            },
                          },
                          '&:hover': {
                            bgcolor: item.active ? 'rgba(var(--color-accent-rgb), 0.1)' : 'rgba(var(--color-text-rgb), 0.05)',
                          },
                          ...(item.pulse
                            ? {
                                animation: 'sidebar-pulse-bg 1.2s ease-in-out infinite',
                                '@keyframes sidebar-pulse-bg': {
                                  '0%, 100%': { backgroundColor: 'rgba(244, 67, 54, 0.08)' },
                                  '50%': { backgroundColor: 'rgba(244, 67, 54, 0.32)' },
                                },
                                '& .pulse-label': {
                                  animation: 'sidebar-pulse-label 1.2s ease-in-out infinite',
                                  fontWeight: '700 !important',
                                },
                                '@keyframes sidebar-pulse-label': {
                                  '0%, 100%': { color: '#ff7961' },
                                  '50%': { color: '#f44336' },
                                },
                                '& .pulse-icon': {
                                  animation: 'sidebar-pulse-icon 1.2s ease-in-out infinite',
                                  color: '#f44336 !important',
                                },
                                '@keyframes sidebar-pulse-icon': {
                                  '0%, 100%': { transform: 'scale(1)' },
                                  '50%': { transform: 'scale(1.18)' },
                                },
                              }
                            : {}),
                        }}
                      >
                        <ListItemIcon
                          className={item.pulse ? 'pulse-icon' : undefined}
                          sx={{
                            minWidth: 32,
                            color: 'var(--color-accent)',
                            transition: 'transform 0.2s ease',
                          }}
                        >
                          {item.icon}
                        </ListItemIcon>
                        <ListItemText
                          primary={item.label}
                          primaryTypographyProps={{
                            className: item.pulse ? 'pulse-label' : undefined,
                            fontWeight: item.active ? 600 : 400,
                            color: 'var(--color-text-strong)',
                            fontSize: '0.8rem',
                          }}
                          secondary={
                            item.tag && isMobile ? (
                              <Box
                                component="span"
                                sx={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  mt: 0.5,
                                  px: 1,
                                  py: 0.35,
                                  borderRadius: '4px',
                                  bgcolor: 'var(--color-accent)',
                                  animation: 'sidebar-early-access-pulse 1.8s ease-in-out infinite',
                                  '@keyframes sidebar-early-access-pulse': {
                                    '0%, 100%': { boxShadow: '0 0 0 0 rgba(var(--color-accent-rgb), 0.5)' },
                                    '50%': { boxShadow: '0 0 0 5px rgba(var(--color-accent-rgb), 0)' },
                                  },
                                }}
                              >
                                <Typography
                                  component="span"
                                  sx={{
                                    fontSize: '0.62rem',
                                    fontWeight: 800,
                                    letterSpacing: 0.6,
                                    textTransform: 'uppercase',
                                    color: '#fff',
                                    lineHeight: 1.3,
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {item.tag.label}
                                </Typography>
                              </Box>
                            ) : undefined
                          }
                          secondaryTypographyProps={{ component: 'div' }}
                        />
                        {item.pulse && !item.tag && (
                          <Box
                            sx={{
                              ml: 1,
                              px: 0.75,
                              py: 0.1,
                              borderRadius: 1,
                              bgcolor: '#f44336',
                              animation: 'sidebar-pulse-badge 1.2s ease-in-out infinite',
                              '@keyframes sidebar-pulse-badge': {
                                '0%, 100%': { boxShadow: '0 0 0 0 rgba(244, 67, 54, 0.7)' },
                                '50%': { boxShadow: '0 0 0 6px rgba(244, 67, 54, 0)' },
                              },
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: '0.6rem',
                                fontWeight: 800,
                                letterSpacing: 0.5,
                                color: '#fff',
                                lineHeight: 1.4,
                              }}
                            >
                              {item.pulseLabel ?? 'LATEST'}
                            </Typography>
                          </Box>
                        )}
                        {item.count !== undefined && (
                          <Box
                            sx={{
                              minWidth: item.countVariant === 'alert' ? 20 : 24,
                              height: 20,
                              borderRadius: '50%',
                              bgcolor: item.countVariant === 'alert' ? 'var(--color-danger)' : 'rgba(var(--color-text-rgb), 0.1)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              ml: 1,
                              px: item.countVariant === 'alert' ? 0.5 : 0.75,
                              boxShadow: item.countVariant === 'alert' ? '0 0 0 1px rgba(255, 255, 255, 0.1)' : 'none',
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 600,
                                color: item.countVariant === 'alert' ? 'var(--color-white)' : 'var(--color-text-strong)',
                                fontSize: '0.7rem',
                              }}
                            >
                              {item.count}
                            </Typography>
                          </Box>
                        )}
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Collapse>

              {/* Divider between groups (except after last) */}
              {groupIndex < groups.length - 1 && (
                <Divider sx={{ my: 0.5, borderColor: 'rgba(var(--color-text-rgb), 0.1)' }} />
              )}
            </React.Fragment>
          );
        })}
        </List>
      </Box>

      {/* Collapse button at bottom (desktop only) */}
      {!isMobile && (
        <Box
          sx={{
            p: 0.75,
            borderTop: '1px solid',
            borderColor: 'rgba(var(--color-text-rgb), 0.1)',
            display: 'flex',
            justifyContent: 'center',
            bgcolor: 'var(--color-bg)',
          }}
        >
          <IconButton
            size="small"
            onClick={onToggle}
            sx={{
              color: 'var(--color-text-strong)',
              '&:hover': {
                bgcolor: 'rgba(var(--color-text-rgb), 0.1)',
              },
            }}
          >
            <ChevronLeftIcon />
          </IconButton>
        </Box>
      )}
    </Box>
  );

  if (isMobile) {
    return (
      <>
        {showMobileToggle && !open && (
          <IconButton
            onClick={onToggle}
            aria-label="Open navigation"
            sx={{
              position: 'fixed',
              top: 16,
              left: 16,
              zIndex: (theme) => theme.zIndex.drawer + 1,
              bgcolor: 'background.paper',
              boxShadow: 2,
              '&:hover': {
                bgcolor: 'action.hover',
              },
            }}
          >
            <MenuIcon />
          </IconButton>
        )}
        <Drawer
          anchor="left"
          open={open}
          onClose={onClose}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile
          }}
          PaperProps={{
            sx: {
              width: drawerWidth,
              maxWidth: '100vw',
              height: '100%',
              maxHeight: '100dvh',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            },
          }}
        >
          {drawerContent}
        </Drawer>
      </>
    );
  }

  return (
    <>
      <Box
        ref={rootRef}
        sx={{
          width: open ? drawerWidth : 0,
          flexShrink: 0,
          transition: 'width 0.3s',
          overflow: 'hidden',
          height: '100%',
          minHeight: 0,
          display: 'flex',
        }}
      >
        <Drawer
          variant="persistent"
          anchor="left"
          open={open}
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            height: '100%',
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              boxSizing: 'border-box',
              position: 'relative',
              height: '100%',
              minHeight: 0,
              borderRight: 'none',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            },
          }}
        >
          {drawerContent}
        </Drawer>
      </Box>

      {/* "Early Access" flag protruding from the sidebar edge, beside its row */}
      {taggedItem?.tag && tagPos && (
        <Box
          onClick={() => taggedItem.onClick()}
          role="button"
          aria-label={taggedItem.tag.label}
          sx={{
            position: 'fixed',
            left: `${tagPos.left}px`,
            top: `${tagPos.top}px`,
            zIndex: (t) => t.zIndex.drawer + 2,
            display: 'flex',
            alignItems: 'center',
            gap: 0.6,
            pl: 1.25,
            pr: 1.5,
            py: 1.75,
            color: '#fff',
            borderRadius: '0 10px 10px 0',
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            background: 'linear-gradient(110deg, #ff8a2b 0%, var(--color-accent) 45%, #ff5e3a 100%)',
            // Two infinite animations: a glowing pulse + a gentle sideways nudge
            // (the nudge keeps the -50% vertical centering in every keyframe).
            animation:
              'earlyAccessTabPulse 1.8s ease-in-out infinite, earlyAccessTabNudge 2.2s ease-in-out infinite',
            '@keyframes earlyAccessTabPulse': {
              '0%, 100%': { boxShadow: '0 6px 18px rgba(0, 0, 0, 0.35), 0 0 0 0 rgba(255, 138, 43, 0.7)' },
              '50%': { boxShadow: '0 6px 22px rgba(255, 94, 58, 0.55), 0 0 0 7px rgba(255, 138, 43, 0)' },
            },
            '@keyframes earlyAccessTabNudge': {
              '0%, 100%': { transform: 'translate(0, -50%)' },
              '50%': { transform: 'translate(5px, -50%)' },
            },
            // Shimmer sweep
            '&::after': {
              content: '""',
              position: 'absolute',
              inset: 0,
              background: 'linear-gradient(120deg, transparent 30%, rgba(255,255,255,0.6) 50%, transparent 70%)',
              transform: 'translateX(-130%)',
              animation: 'earlyAccessShimmer 2.8s ease-in-out infinite',
            },
            '@keyframes earlyAccessShimmer': {
              '0%': { transform: 'translateX(-130%)' },
              '55%, 100%': { transform: 'translateX(130%)' },
            },
            '& .ea-sparkle': {
              animation: 'earlyAccessSparkle 1.6s ease-in-out infinite',
            },
            '@keyframes earlyAccessSparkle': {
              '0%, 100%': { opacity: 1, transform: 'scale(1) rotate(0deg)' },
              '50%': { opacity: 0.65, transform: 'scale(1.3) rotate(18deg)' },
            },
            transition: 'filter 0.2s ease, padding-right 0.2s ease',
            '&:hover': { filter: 'brightness(1.1) saturate(1.1)', pr: 2 },
          }}
        >
          <AutoAwesomeIcon className="ea-sparkle" sx={{ position: 'relative', zIndex: 1, fontSize: 14 }} />
          <Typography
            sx={{
              position: 'relative',
              zIndex: 1,
              fontSize: '0.64rem',
              fontWeight: 900,
              letterSpacing: 0.7,
              textTransform: 'uppercase',
              lineHeight: 1.3,
              textShadow: '0 1px 2px rgba(0, 0, 0, 0.25)',
            }}
          >
            {taggedItem.tag.label}
          </Typography>
        </Box>
      )}
    </>
  );
};

export default SidebarNavigation;
